<template>
    <li
        class="SidebarMenuItem"
        :class="[ {'SidebarMenuItem--active': active}, {'SidebarMenuItem--disabled': disabled} , additionalClass ]"
    >
        <a
            :href="url"
            :target="target"
            class="SidebarMenuItem__link"
            @click="emitClick"
            @mouseenter="$emit('mouseenter')"
            @mouseleave="$emit('mouseleave')"
        >
            <div class="SidebarMenuItem__linkContent">
                <span v-if="currentIcon" class="SidebarMenuItem__linkIcon">
                    <EidIcon :icon-src="currentIcon" :icon-props="iconProps" :default-icon-svg="defaultIconSvg" />
                </span>
                <slot name="label">
                    <span class="SidebarMenuItem__linkText">
                        {{ label }}
                    </span>
                </slot>
            </div>
        </a>
        <div v-if="processing" class="SidebarMenuItem__notificationBadgeContainer">
            <div class="SidebarMenuItem__processingBadge">
                <EidIcon class="SidebarMenuItem__processingSpinner animate-spin" :icon-src="faSpinner" />
            </div>
        </div>
        <div v-else-if="showNotificationBadge" class="SidebarMenuItem__notificationBadgeContainer">
            <div
                class="SidebarMenuItem__notificationBadge"
                :class="{'SidebarMenuItem__notificationBadge--max': notificationCount > 99}"
            >
                {{ notificationCount > 99 ? '99+' : notificationCount }}
            </div>
        </div>
        <Menu v-if="open && hasSubNavigation">
            <slot />
        </Menu>
    </li>
</template>

<style lang="scss">
@import "@/styles/utilities/colormix.function.scss";

.SidebarMenuItem {
    position: relative;
    list-style: none;

    &__link {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 72px;
        border-radius: 8.75px;
        text-decoration: none;
        color: var(--eidWhite);

        &:hover {
            background: transparent-color(var(--eidWhite), 10%);
        }
    }

    &--active &__link {
        background: linear-gradient(var(--eidSafetyBlue), var(--eidTrustGreen));

        &:hover {
            background: linear-gradient(var(--eidSafetyBlue), var(--eidTrustGreen));
        }
    }

    &__linkContent {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 4px;
        text-align: center;
    }

    &__linkIcon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        font-size: 24px;

        img, svg {
            width: 100%;
            height: 100%;
            max-width: 28px;
            max-height: 28px;
            object-fit: contain;
        }
    }

    &__linkText {
        font-size: 12px;
        font-weight: 500;
        color: var(--eidWhite);
    }

    &__notificationBadgeContainer {
        position: absolute;
        top: 8px;
        right: 12px;
        pointer-events: none;
    }

    &__notificationBadge {
        display: flex;
        justify-content: center;
        align-items: center;
        min-width: 20px;
        height: 20px;
        padding: 0 4px;
        border-radius: 100px;
        font-size: 12px;
        font-weight: 600;
        background-color: var(--eidDangerRed);
        color: var(--eidWhite);

        &--max {
            font-size: 10px;
        }
    }

    &__processingBadge {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: var(--eidTrustGreen);
        color: var(--eidWhite);
        font-size: 12px;
    }

    &__processingSpinner {
        flex-shrink: 0;
        color: var(--eidWhite);
        font-size: 12px;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
    import { faSpinner } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import Menu from '@evidentid/dashboard-commons/components/Menu/Menu.vue';

    interface NotificationConfig {
        fetchCount: (() => Promise<void>) | null;
        getCount: (() => number) | null;
    }

    export default defineComponent({
        name: 'SidebarMenuItem',
        components: { Menu, EidIcon },
        props: {
            active: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            additionalClass: {
                type: String as PropType<string>,
                default: '',
            },
            disabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            open: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            href: {
                type: String as PropType<string | null>,
                default: null,
            },
            target: {
                type: String as PropType<string | null>,
                default: null,
            },
            // FontAwesome icon or image URL
            icon: {
                type: [ Object, String ] as PropType<IconDefinition | string>,
                default: null,
            },
            // this was added due to supporting icon being fetched on url, in case of failure we show a default icon
            defaultIconSvg: {
                type: [ Object, String ] as PropType<IconDefinition | string | null>,
                default: null,
            },
            // FontAwesome icon or image URL
            iconActive: {
                type: [ Object, String ] as PropType<IconDefinition | string>,
                default: null,
            },
            iconProps: {
                type: Object as PropType<object>,
                default: (): object => ({}),
            },
            label: {
                type: String as PropType<string>,
                default: '',
            },
            notificationConfig: {
                type: Object as PropType<NotificationConfig | null>,
                default: null,
            },
            processing: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'click', 'mouseenter', 'mouseleave' ],
        data() {
            return { faSpinner };
        },
        computed: {
            showNotificationBadge(): boolean {
                if (!this.notificationConfig) {
                    return false;
                }
                return this.notificationCount > 0;
            },
            notificationCount(): number {
                return this.notificationConfig?.getCount?.() || 0;
            },
            hasSubNavigation(): boolean {
                return Boolean(this.$slots.default);
            },
            currentIcon(): IconDefinition | string {
                return this.active ? this.iconActive || this.icon : this.icon;
            },
            url(): string {
                return this.href || 'javascript:void(0)';
            },
        },
        async mounted() {
            if (this.notificationConfig?.fetchCount) {
                await this.notificationConfig.fetchCount();
            }
        },
        methods: {
            emitClick(event: MouseEvent) {
                if (!this.disabled) {
                    this.$emit('click', event);
                }
            },
        },
    });
</script>
